import { useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Column, InputSelectField, Radio, RadioGroup, TextField, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { SectionEditDialog } from '~/components/shared/AccountArea';
import {
  buildShiftDetailsPatch,
  isAssistedHygieneAnswered,
  isTimePerPatientValid,
  requiredOk,
  toSectionDefaults,
  useUpdateLocation,
  type IShiftDetailsValues,
} from '~/domain/LocationSettings';
import { timePerPatientOptions, toSelectOptions } from '~/utils/Enum';
import type { ISectionModalProps } from './types';

const TIME_FIELDS = [
  { name: 'timePerPatientRDH', label: 'Dental Hygienist' },
  { name: 'timePerPatientDA', label: 'Dental Assistant' },
  { name: 'timePerPatientDN', label: 'Associate Dentist' },
] as const;

const TIME_OPTIONS = toSelectOptions(timePerPatientOptions);

const YES = 'yes';
const NO = 'no';

/**
 * Time per patient, assisted hygiene and notes. Scrub colour, left-handed
 * accommodations and standard shift hours wait for a backend.
 */
export const ShiftDetailsModal = ({ officeId, office, onClose, onSaved }: ISectionModalProps) => {
  const defaultValues = useMemo(() => toSectionDefaults(office, null).shiftDetails, [office]);
  const { control, handleSubmit, formState } = useForm<IShiftDetailsValues>({
    defaultValues,
    mode: 'onChange',
  });
  const values = useWatch({ control }) as IShiftDetailsValues;
  const { dirtyFields } = formState;
  const updateLocation = useUpdateLocation(officeId);

  const isAnyTimeDirty = TIME_FIELDS.some(({ name }) => dirtyFields[name]);
  const canSave =
    formState.isDirty &&
    requiredOk(isAnyTimeDirty, isTimePerPatientValid(values)) &&
    requiredOk(dirtyFields.isAssistedHygiene, isAssistedHygieneAnswered(values));

  // Only the changed fields go out, so an untouched field can't overwrite the stored one.
  const save = handleSubmit(async (submitted) => {
    const changes = Object.fromEntries(
      (Object.keys(submitted) as (keyof IShiftDetailsValues)[])
        .filter((key) => dirtyFields[key])
        .map((key) => [key, submitted[key]]),
    ) as Partial<IShiftDetailsValues>;
    await updateLocation.mutateAsync(buildShiftDetailsPatch(changes, office));
    onSaved();
  });

  let hygieneAnswer = '';
  if (values.isAssistedHygiene === true) hygieneAnswer = YES;
  if (values.isAssistedHygiene === false) hygieneAnswer = NO;

  return (
    <SectionEditDialog
      title="Edit Shift Details"
      dataCy="edit-shift-details"
      onClose={onClose}
      onSave={save}
      canSave={canSave}
    >
      <Column gap={3}>
        <Typography variant="overline">Time per patient</Typography>
        <Typography variant="body1" color={colors.text.secondary}>
          We track this for every role at every location, whether or not you book pros for that
          role.
        </Typography>
        {TIME_FIELDS.map(({ name, label }) => (
          <Controller
            key={name}
            name={name}
            control={control}
            render={({ field }) => (
              <InputSelectField
                label={label}
                options={TIME_OPTIONS}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        ))}
      </Column>
      <Controller
        name="isAssistedHygiene"
        control={control}
        render={({ field }) => (
          <RadioGroup
            label="Assisted hygiene available?"
            value={hygieneAnswer}
            onChange={(answer) => field.onChange(answer === YES)}
            orientation="horizontal"
          >
            <Radio value={YES} label="Yes" data-cy="assisted-hygiene-yes" />
            <Radio value={NO} label="No" data-cy="assisted-hygiene-no" />
          </RadioGroup>
        )}
      />
      <Column gap={3}>
        <Typography variant="overline">Notes to professionals</Typography>
        <Typography variant="body1" color={colors.text.secondary}>
          This note will display on each Shift Details page and be sent pros after confirmation.
        </Typography>
        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
            <TextField
              label="Notes"
              placeholder="Ex: Check in at the front desk."
              multiline
              rows={4}
              data-cy="location-notes"
              {...field}
            />
          )}
        />
      </Column>
    </SectionEditDialog>
  );
};
